import React, { useState } from "react";
import { formatEuro, api, STATUS_LABELS, formatArrivalDate } from "../lib/api";
import { Pencil, Trash2, MoveRight, Trophy, X, Calendar } from "lucide-react";
import { toast } from "sonner";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export default function OpportunityCard({ op, isAdmin, onChanged, onEdit }) {
    const [busy, setBusy] = useState(false);

    const move = async (newStatus) => {
        if (newStatus === op.status) return;
        setBusy(true);
        try {
            await api.patch(`/opportunities/${op.id}/status`, { status: newStatus });
            toast.success(`Movido a ${STATUS_LABELS[newStatus]}`);
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo mover");
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("¿Eliminar esta oportunidad?")) return;
        setBusy(true);
        try {
            await api.delete(`/opportunities/${op.id}`);
            toast.success("Oportunidad eliminada");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo eliminar");
        } finally {
            setBusy(false);
        }
    };

    const statusBar = {
        interesado: "status-bar-interesado",
        posible_cierre: "status-bar-posible",
        ganado: "status-bar-ganado",
        perdido: "status-bar-perdido",
    }[op.status];

    return (
        <div
            className="brutal-card stagger-item relative"
            data-testid={`opportunity-card-${op.id}`}
        >
            <div className={`h-1 w-full ${statusBar}`} />
            <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-base leading-tight pr-2">
                        {op.client_name}
                    </h3>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid={`card-menu-${op.id}`}
                                disabled={busy}
                                className="p-1 border border-transparent hover:border-black/20 transition-colors"
                                aria-label="Acciones"
                            >
                                <MoveRight className="w-4 h-4" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className="bg-white border border-black/15 rounded-none p-1 min-w-[200px]"
                        >
                            {op.status !== "interesado" && (
                                <DropdownMenuItem
                                    onClick={() => move("interesado")}
                                    className="text-sm cursor-pointer rounded-none"
                                    data-testid={`move-interesado-${op.id}`}
                                >
                                    <MoveRight className="w-3.5 h-3.5 mr-2" />
                                    Mover a Muy interesados
                                </DropdownMenuItem>
                            )}
                            {op.status !== "posible_cierre" && (
                                <DropdownMenuItem
                                    onClick={() => move("posible_cierre")}
                                    className="text-sm cursor-pointer rounded-none"
                                    data-testid={`move-posible-${op.id}`}
                                >
                                    <MoveRight className="w-3.5 h-3.5 mr-2" />
                                    Mover a Cierre 7 días
                                </DropdownMenuItem>
                            )}
                            {isAdmin && (
                                <>
                                    <DropdownMenuSeparator />
                                </>
                            )}
                            {op.status !== "ganado" && (
                                <DropdownMenuItem
                                    onClick={() => move("ganado")}
                                    className="text-sm cursor-pointer rounded-none text-[#008F11]"
                                    data-testid={`move-ganado-${op.id}`}
                                >
                                    <Trophy className="w-3.5 h-3.5 mr-2" />
                                    Marcar Ganado
                                </DropdownMenuItem>
                            )}
                            {op.status !== "perdido" && (
                                <DropdownMenuItem
                                    onClick={() => move("perdido")}
                                    className="text-sm cursor-pointer rounded-none text-[#D32F2F]"
                                    data-testid={`move-perdido-${op.id}`}
                                >
                                    <X className="w-3.5 h-3.5 mr-2" />
                                    Marcar Perdido
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={() => onEdit?.(op)}
                                className="text-sm cursor-pointer rounded-none"
                                data-testid={`edit-${op.id}`}
                            >
                                <Pencil className="w-3.5 h-3.5 mr-2" />
                                Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={remove}
                                className="text-sm cursor-pointer rounded-none text-red-600"
                                data-testid={`delete-${op.id}`}
                            >
                                <Trash2 className="w-3.5 h-3.5 mr-2" />
                                Eliminar
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <div className="mt-3 flex items-baseline justify-between gap-2">
                    <span className="font-display text-2xl tracking-tight">
                        {formatEuro(op.amount)}
                    </span>
                    {op.arrival_date && (
                        <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
                            <Calendar className="w-3 h-3" />
                            {formatArrivalDate(op.arrival_date)}
                        </span>
                    )}
                </div>

                {op.notes && (
                    <p className="mt-3 text-xs text-neutral-600 line-clamp-2">{op.notes}</p>
                )}

                <div className="mt-4 pt-3 border-t border-black/10 flex items-center justify-between">
                    <span className="label-eyebrow text-neutral-500">Agente</span>
                    <span className="text-xs font-medium">{op.owner_name}</span>
                </div>
            </div>
        </div>
    );
}
